import Phaser from 'phaser';
import { CameraController } from '../camera/CameraController';
import type { GameConfig } from '../config/gameConfig';
import { CoordinateSystem } from '../core/coords';
import { IsoProjection } from '../core/iso';
import { SimulationClock } from '../core/SimulationClock';
import type { GridPoint, ScreenPoint, WorldPoint } from '../core/types';
import type { ActionBus, GameAction } from '../input/actions';
import { InputManager } from '../input/InputManager';
import { buildingThumbnail, generateBuildingArt } from '../render/buildingArt';
import { BuildingRenderer, type DamageState } from '../render/BuildingRenderer';
import type { DefenceEvent } from '../sim/defence/Defence';
import type { MatchEvent } from '../sim/modes/MatchRules';
import { DamageNumbers, Flashes, generateFxArt } from '../render/combatFx';
import { MapRenderer, type MarkArea } from '../render/MapRenderer';
import { OWNER_COLORS, TERRAIN_PALETTE, UI_COLORS } from '../render/palette';
import { generateArt } from '../render/proceduralArt';
import type { Building } from '../sim/buildings/BuildingStore';
import { ECONOMY, PLAYER_BUILDABLE, REPAIR, STOCK_RESOURCES, buildingDef, formatCost, isBuildingTypeId, statsAt, type BuildingTypeId, type StockResource } from '../sim/buildings/definitions';
import type { EconomyEvent } from '../sim/economy/Economy';
import type { ColonyEvent } from '../sim/colony/Colony';
import { INFECTION, type InfectedEvent } from '../sim/infected/InfectedDirector';
import { UnitRenderer } from '../render/UnitRenderer';
import { generateUnitArt } from '../render/unitArt';
import type { Unit, UnitEvent } from '../sim/units/UnitStore';
import { TRAIN_QUEUE_LIMIT, unitDef, type UnitTypeId } from '../sim/units/unitDefs';
import type { CommandResult, CommandSink } from '../sim/commands';
import type { PlayerId } from '../sim/ids';
import type { Simulation } from '../sim/Simulation';
import { BuildMode } from '../systems/BuildMode';
import { SelectionSystem } from '../systems/SelectionSystem';
import type { BuildingInspection, ColonyStatus, ColonyView, Hud, TileInspection, TrainingView, UnitsView } from '../ui/Hud';
import type { TileMetadata, WorldMap } from '../world/WorldMap';

export interface GameContext {
  readonly config: GameConfig;
  readonly map: WorldMap;
  readonly bus: ActionBus;
  readonly hud: Hud;
  /** The game simulation (rules + state). The scene only renders it and sends it commands. */
  readonly sim: Simulation;
  /** Where commands go: the local sim now, the server in Phase 10+. */
  readonly sink: CommandSink;
  /** The player controlled on this device. */
  readonly localPlayer: PlayerId;
  /** Number of live Phaser.Game instances (must always be 1). */
  readonly liveGames: () => number;
  /** Online match (Phase 10): the sim is stepped only by server ticks through this session. */
  readonly net?: import('../net/NetSession').NetSession;
}

/** Read-only debug surface used by automated browser tests and the dev console. */
export interface GameDebugApi {
  ready: boolean;
  renderer: 'webgl' | 'canvas';
  state(): {
    zoom: number;
    targetZoom: number;
    zooming: boolean;
    scrollX: number;
    scrollY: number;
    viewWidth: number;
    viewHeight: number;
    selected: GridPoint | null;
    hovered: GridPoint | null;
    selectedBuilding: number | null;
    gridVisible: boolean;
    passabilityVisible: boolean;
    ticks: number;
    objects: number;
    chunks: { visible: number; total: number };
    mapSize: { width: number; height: number };
    tileSize: { width: number; height: number };
    /** Player start slots in use (owner = player id). */
    starts: { owner: string; tile: GridPoint }[];
    localPlayer: string;
    buildRadius: number;
    inputMode: 'mouse' | 'touch';
    pinching: boolean;
  };
  diagnostics(): {
    liveGames: number;
    busListeners: number;
    cameraListeners: number;
    pointerDown: number;
    pointerMove: number;
    pointerUp: number;
    wheel: number;
    keyDown: number;
    resize: number;
    sceneUpdates: number;
    loopFrames: number;
  };
  buildings(): Building[];
  /** A player's ledger (default: the local player). */
  resources(playerId?: string): { amounts: Record<StockResource, number>; capacity: Record<StockResource, number>; shortage: boolean };
  economy(): ReturnType<Simulation['economy']['snapshot']>;
  colony(playerId?: string): { population: number; capacity: number; assigned: number; idle: number; foodNeeded: number };
  workersAt(id: number): number;
  units(): Unit[];
  selectedUnits(): number[];
  boxMode(): boolean;
  /** Phase 6 combat: projectiles in flight, a building's drawn damage state, attack-move armed, live fx counts. */
  projectiles(): { id: number; x: number; y: number; ownerId: string }[];
  damageState(buildingId: number): DamageState | null;
  attackMoveArmed(): boolean;
  /** Phase 10: online session state (null offline). */
  net(): { room: string; me: string | null; tick: number; backlog: number; desyncs: number; connected: boolean } | null;
  /** Phase 9: mode, result, players' status, time left. */
  match(): { mode: string; result: { winners: string[]; reason: string } | null; status: Record<string, string>; timeLeft: number | null };
  /** Messages toasted recently, oldest first. */
  toasts(): string[];
  /** Phase 7: the infected side (read-only). */
  infection(): { count: number; infected: number[]; exposure: Record<string, number>; secondsToWave: number | null; pendingRises: number };
  fx(): { flashes: number; damageNumbers: number; tracers: number; bursts: number };
  /** Screen position of a unit's feet as drawn this frame. */
  unitScreen(id: number): ScreenPoint | null;
  trainingQueue(id: number): { unit: string; progress: number }[];
  productionStatus(id: number): { remaining: number; stalled: boolean } | null;
  /** Floating production labels currently animating. */
  floaters(): number;
  buildMode(): { active: string | null; pinned: boolean; preview: { ok: boolean; origin: GridPoint; failures: string[]; message: string; tiles: { x: number; y: number; ok: boolean }[] } | null };
  /** Screen-space bounds of a building's rendered sprite. */
  buildingSpriteScreenBounds(id: number): { x: number; y: number; width: number; height: number } | null;
  /** Screen position of the world point a building sprite is anchored to (footprint centre). */
  buildingAnchorScreen(id: number): ScreenPoint | null;
  /** Screen-space bounds of the placement preview ghost (null when hidden). */
  previewScreenBounds(): { x: number; y: number; width: number; height: number } | null;
  /** Read-only placement check for the local player (same rules the sim enforces). */
  checkPlacement(type: string, originX: number, originY: number): { ok: boolean; failures: string[]; message: string };
  /** Test helper: centre the CAMERA on a tile (never changes game state). */
  centerOn(x: number, y: number): void;
  tileToScreen(x: number, y: number): ScreenPoint;
  screenToTile(x: number, y: number): GridPoint;
  screenToWorld(x: number, y: number): WorldPoint;
  worldToScreen(x: number, y: number): ScreenPoint;
  tileCornersOnScreen(x: number, y: number): ScreenPoint[];
  terrainAt(x: number, y: number): string;
  phaserWorldPoint(x: number, y: number): WorldPoint;
  groundTileBounds(x: number, y: number): { x: number; y: number; width: number; height: number } | null;
  tileMetadata(x: number, y: number): TileMetadata | null;
}

const FLOAT_COLOR: Record<StockResource, string> = { wood: '#e8c08a', scrap: '#d3d8de', food: '#f5d77d' };

/** Build in which this code is (for "arrives in Phase N" notes). */
const CURRENT_PHASE = 9;

/** "+5 wood every 10 s" at full staffing, plus the effective rate when partly staffed. */
function productionText(rule: { resource: string; amount: number; intervalSeconds: number }, slots: number, workers: number | null): string {
  const full = `+${rule.amount} ${rule.resource} every ${rule.intervalSeconds} s with ${slots} workers`;
  if (workers === null || workers === slots || slots === 0) return full;
  if (workers === 0) return `${full} (no workers now)`;
  return `${full} (now every ${Math.round((rule.intervalSeconds * slots) / workers)} s with ${workers})`;
}

function isPlayerIdLike(v: string): boolean {
  return /^p[1-4]$/.test(v);
}

/** Infection status line for the unit card. */
function infectionStatus(u: Unit, sim: Simulation): { status?: string; statusBad?: boolean } {
  if (unitDef(u.type).infected) return {};
  if (sim.infected.isInfected(u.id)) return { status: `Infected — loses ${INFECTION.bleedPerSecond} health/s and will turn if it dies`, statusBad: true };
  const x = sim.infected.exposureOf(u.id);
  return x > 0 ? { status: `Bitten ${x} / ${INFECTION.infectAt} — infected at ${INFECTION.infectAt} bites`, statusBad: false } : {};
}

/** What a unit is doing, in words. */
function orderText(u: Unit, sim: Simulation): string {
  const o = u.order;
  if (unitDef(u.type).infected) {
    if (o.kind === 'attackMove') return `Shambling towards ${o.target.x}, ${o.target.y}`;
    if (o.kind === 'idle') return 'Standing still';
  }
  switch (o.kind) {
    case 'move':
      return `Moving to ${o.target.x}, ${o.target.y}`;
    case 'attackMove':
      return `Attack-moving to ${o.target.x}, ${o.target.y}`;
    case 'repair': {
      const b = sim.buildings.get(o.buildingId);
      return `Repairing ${b ? statsAt(b.type, b.level).name : 'a building'}`;
    }
    case 'attack': {
      const name =
        o.target.kind === 'unit'
          ? (() => {
              const t = sim.units.get(o.target.id);
              return t ? `enemy ${unitDef(t.type).name}` : 'target';
            })()
          : (() => {
              const b = sim.buildings.get(o.target.id);
              return b ? `enemy ${buildingDef(b.type).name}` : 'target';
            })();
      return o.auto ? `Engaging ${name}` : `Attacking ${name}`;
    }
    default:
      return u.stance === 'hold' ? 'Holding position' : 'Idle — will engage enemies in sight';
  }
}

function refundText(a: Partial<Record<StockResource, number>>): string | undefined {
  return STOCK_RESOURCES.some((r) => (a[r] ?? 0) > 0) ? formatCost(a) : undefined;
}

declare global {
  interface Window {
    __AFTER_THE_FALL__?: GameDebugApi;
  }
}

/**
 * Composition root and the ONE update loop. Game rules and state live in the
 * Simulation; this scene renders it, turns input into commands for the
 * CommandSink, and shows the results.
 */
export class GameScene extends Phaser.Scene {
  private iso!: IsoProjection;
  private coords!: CoordinateSystem;
  private mapRenderer!: MapRenderer;
  private buildingRenderer!: BuildingRenderer;
  private camera!: CameraController;
  private selection!: SelectionSystem;
  private buildMode!: BuildMode;
  private inputs!: InputManager;
  private clock!: SimulationClock;
  private debugTimer = 0;
  private econTimer = 0;
  private colonyOpen = false;
  private unitRenderer!: UnitRenderer;
  /** Units the local player has selected (own units), or one enemy unit being inspected. */
  private selectedUnits = new Set<number>();
  private inspectedEnemy: number | null = null;
  private boxMode = false;
  /** Attack-move armed: the next map tap sends the selected units, fighting on the way. */
  private attackMoveArmed = false;
  private flashes!: Flashes;
  private damageNumbers!: DamageNumbers;
  /** Scene time of the last "under attack" alert (throttled). */
  private lastAlert = -Infinity;
  private lastInfectAlert = -Infinity;
  /** The 30-second horde warning was given for this wave time. */
  private warnedWave = -1;
  /** Scene update() calls; must advance exactly once per game-loop frame. */
  private updates = 0;
  private readonly unsubs: (() => void)[] = [];

  constructor(private readonly ctx: GameContext) {
    super({ key: 'GameScene' });
  }

  create(): void {
    const { config, map, bus, hud, sim } = this.ctx;
    this.iso = new IsoProjection(config.tile.width, config.tile.height);
    this.cameras.main.setBackgroundColor(UI_COLORS.background);

    generateArt(this, config.tile.width, config.tile.height);
    const buildingArt = generateBuildingArt(this, config.tile.width, config.tile.height);
    generateFxArt(this);
    this.flashes = new Flashes(this);
    this.damageNumbers = new DamageNumbers(this);
    this.mapRenderer = new MapRenderer(this, map, this.iso, config.render.chunkSize);
    this.buildingRenderer = new BuildingRenderer(this, this.iso, sim.buildings, buildingArt, (tiles, covered) => this.mapRenderer.setTilesCovered(tiles, covered), this.flashes, this.damageNumbers);
    this.selection = new SelectionSystem(map, (x, y) => sim.buildings.at(x, y)?.id ?? null);
    this.buildMode = new BuildMode(sim, this.ctx.sink, this.ctx.localPlayer);
    this.unitRenderer = new UnitRenderer(this, this.iso, sim.units, generateUnitArt(this), this.flashes, this.damageNumbers);
    this.clock = new SimulationClock(config.simulation.ticksPerSecond, config.simulation.maxTicksPerFrame);
    this.camera = new CameraController(this.cameras.main, config.camera, this.iso, map.width, map.height);
    this.coords = new CoordinateSystem(this.iso, map.width, map.height, () => this.camera.view());
    // A touch press on the pinned build preview drags the preview instead of panning the map.
    this.inputs = new InputManager(
      this,
      bus,
      config.input,
      config.camera,
      (screen) => this.buildMode.state.pinned && this.buildMode.previewCovers(this.coords.screenToTileUnbounded(screen)),
      // Box mode (or Shift with a mouse): drags draw a selection box instead of panning.
      (shift) => !this.buildMode.isActive && (this.boxMode || shift),
    );

    hud.setBuildCatalog(
      PLAYER_BUILDABLE.map((d) => ({
        id: d.id,
        name: d.name,
        hotkey: d.hotkey ?? '',
        size: `${d.footprint.width}×${d.footprint.height}`,
        cost: d.cost,
        costText: formatCost(d.cost),
        thumbnail: buildingThumbnail(this, d.id),
      })),
    );

    this.unsubs.push(
      bus.on((a) => this.handle(a)),
      this.clock.onTick((_tick, dt) => {
        sim.step(dt);
        this.unitRenderer.syncTick();
      }),
      sim.units.subscribe((e) => this.onUnits(e)),
      // Camera moved (pan, zoom animation, resize, clamp): the tile under a still cursor changed.
      this.camera.onChange(() => {
        hud.setZoom(this.camera.zoom);
        this.mapRenderer.updateCulling(this.viewRect());
        this.refreshHover();
      }),
      this.selection.subscribe((s) => {
        this.mapRenderer.setHover(this.buildMode.isActive ? null : s.hovered, s.hoveredBuilding ? this.areaOf(s.hoveredBuilding) : null);
        this.mapRenderer.setSelected(s.selected, s.building ? this.areaOf(s.building) : null);
        hud.setInspection(s.selected ? this.inspectTile(s.selected) : null, s.building ? this.inspectBuilding(s.building) : null);
        if (s.selected && this.colonyOpen) this.setColonyOpen(false); // one panel at a time
        if (s.selected && (this.selectedUnits.size || this.inspectedEnemy !== null)) this.setUnitSelection([], true);
        const occupant = s.hoveredBuilding ? sim.buildings.definition(s.hoveredBuilding)?.name ?? null : null;
        hud.setCursor(s.hovered ? map.tileMetadata(s.hovered.x, s.hovered.y) : null, this.inputs.pointerPosition !== null, occupant);
      }),
      this.buildMode.subscribe((b) => {
        this.buildingRenderer.setPreview(b.preview);
        hud.setBuildMode({ active: b.active, message: b.preview?.message ?? '', valid: b.preview ? b.preview.ok : null, pinned: b.pinned });
        if (b.active) this.mapRenderer.setHover(null);
      }),
      sim.economy.subscribe((e) => this.onEconomy(e)),
      sim.infected.subscribe((e) => this.onInfected(e)),
      sim.defence.subscribe((e) => this.onDefence(e)),
      sim.match.subscribe((e) => this.onMatch(e)),
      sim.colony.subscribe((e) => this.onColony(e)),
      sim.buildings.subscribe((e) => {
        if (e.type === 'damaged' && e.building.ownerId === this.ctx.localPlayer) this.underAttack();
        if (e.type === 'removed' && e.destroyed) {
          const name = buildingDef(e.building.type).name;
          if (e.building.ownerId === this.ctx.localPlayer) this.ctx.hud.toast(`Your ${name} was destroyed.`, 'error');
          else this.ctx.hud.toast(`Enemy ${name} destroyed.`, 'info');
        }
        if (e.type === 'added' || e.type === 'removed') {
          this.selection.refresh();
          this.buildMode.revalidate();
        } else if (this.selection.state.building === e.building.id) {
          if (e.type === 'upgradeStarted' || e.type === 'upgraded' || e.type === 'completed') this.selection.refresh();
          else hud.updateBuildingLive(e.building);
        }
        if (e.type === 'upgraded' && e.building.ownerId === this.ctx.localPlayer) this.ctx.hud.toast(`Upgraded to ${statsAt(e.building.type, 2).name}.`, 'ok');
      }),
    );

    this.refreshResources();
    this.refreshColony();
    this.refreshArmy();
    this.resetView();
    hud.setGrid(this.mapRenderer.gridVisible);
    hud.setPassability(this.mapRenderer.passabilityVisible);

    this.scale.on(Phaser.Scale.Events.RESIZE, this.onResize, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    this.installDebugApi();
  }

  override update(_time: number, _smoothedDeltaMs: number): void {
    // Raw (unsmoothed) frame time: Phaser's smoothed delta clamps long frames,
    // which would slow the simulation on slow devices.
    const dt = Math.min(this.game.loop.rawDelta / 1000, 0.25);
    this.updates++;
    this.inputs.update(dt);
    this.camera.update(dt);
    if (this.ctx.net) {
      this.ctx.net.pump((step) => {
        this.ctx.sim.step(step);
        this.unitRenderer.syncTick();
      });
      this.unitRenderer.render(this.ctx.net.alpha);
    } else {
      this.clock.advance(dt * this.ctx.config.simulation.speed);
      this.unitRenderer.render(this.clock.alpha);
    }
    this.buildingRenderer.update(this.time.now);

    // Live production countdown for the selected building (4 Hz is plenty for whole seconds).
    this.econTimer -= dt;
    if (this.econTimer <= 0) {
      this.econTimer = 0.25;
      const id = this.selection.state.building;
      if (id !== null) this.ctx.hud.updateBuildingEconomy(id, this.economyStatus(id));
      if (this.colonyOpen) this.refreshColony();
      if (id !== null) {
        const t = this.trainingView(id);
        if (t) this.ctx.hud.updateTraining(id, t);
        const sb = this.ctx.sim.buildings.get(id);
        const up = sb ? buildingDef(sb.type).upgrade : undefined;
        if (sb && up && sb.upgradeProgress !== null) this.ctx.hud.updateUpgrade(id, sb.upgradeProgress / up.seconds);
      }
      if (this.selectedUnits.size || this.inspectedEnemy !== null) this.ctx.hud.setUnits(this.unitsView());
      this.ctx.hud.setTimer(this.ctx.sim.match.result ? null : this.ctx.sim.match.timeLeft);
      const toWave = this.ctx.sim.infected.secondsToWave;
      if (toWave !== null && toWave <= 30 && toWave > 0) {
        const at = Math.round(this.ctx.sim.tick / 10 + toWave);
        if (at !== this.warnedWave) {
          this.warnedWave = at;
          this.ctx.hud.toast('A horde of infected will emerge in 30 seconds.', 'info');
        }
      }
    }

    if (this.ctx.hud.debugVisible) {
      this.debugTimer -= dt;
      if (this.debugTimer <= 0) {
        this.debugTimer = 0.25;
        this.ctx.hud.setDebug(this.debugText());
      }
    }
  }

  // ------------------------------------------------------------------

  private handle(a: GameAction): void {
    switch (a.type) {
      case 'primary':
        this.ctx.hud.setInputMode(a.pointer === 'touch' ? 'touch' : 'mouse');
        if (this.buildMode.isActive) {
          const tile = this.coords.screenToTileUnbounded(a.screen);
          if (a.pointer === 'touch') {
            // Touch has no hover: a tap only moves the preview; the confirm button builds.
            this.buildMode.pinCursor(tile);
          } else {
            this.buildMode.setCursor(tile);
            this.report(this.buildMode.place(), true);
          }
        } else if (this.attackMoveArmed && this.selectedUnits.size) {
          this.attackMoveTap(a.screen, a.pointer === 'touch');
        } else if (!this.unitTap(a.screen, a.pointer === 'touch', false)) {
          const tile = this.selection.select(this.coords.screenToTile(a.screen));
          if (tile && a.pointer === 'touch') this.keepClearOfPanels(tile);
        }
        break;
      case 'secondary':
        if (this.buildMode.isActive) this.buildMode.cancel();
        else if (this.attackMoveArmed) this.setAttackMove(false);
        else if (this.selectedUnits.size) this.unitTap(a.screen, false, true);
        break;
      case 'boxSelect':
        this.boxSelect(a.from, a.to, a.done);
        break;
      case 'toggleBoxMode':
        this.boxMode = !this.boxMode;
        this.refreshArmy();
        break;
      case 'selectArmy':
        this.setUnitSelection(this.ctx.sim.units.ownedBy(this.ctx.localPlayer).map((u) => u.id));
        break;
      case 'stopUnits':
        if (this.selectedUnits.size) this.unitCommand({ type: 'stopUnits', playerId: this.ctx.localPlayer, unitIds: [...this.selectedUnits] });
        break;
      case 'deselectUnits':
        this.setUnitSelection([]);
        break;
      case 'toggleAttackMove':
        if (this.selectedUnits.size) this.setAttackMove(!this.attackMoveArmed);
        else this.ctx.hud.toast('Select units first (tap a unit or Army).', 'info');
        break;
      case 'setStance': {
        const mine = [...this.selectedUnits];
        if (!mine.length) break;
        let stance = a.stance;
        if (stance === 'toggle') stance = this.ctx.sim.units.get(mine[0]!)?.stance === 'hold' ? 'aggressive' : 'hold';
        this.unitCommand({ type: 'setStance', playerId: this.ctx.localPlayer, unitIds: mine, stance });
        this.ctx.hud.setUnits(this.unitsView());
        break;
      }
      case 'trainUnit': {
        const r = this.ctx.sink.submit({ type: 'trainUnit', playerId: this.ctx.localPlayer, buildingId: a.buildingId, unit: a.unit });
        if (r.ok) this.refreshTraining(a.buildingId);
        else this.ctx.hud.toast(r.message, 'error');
        break;
      }
      case 'cancelTraining': {
        const r = this.ctx.sink.submit({ type: 'cancelTraining', playerId: this.ctx.localPlayer, buildingId: a.buildingId });
        if (r.ok) this.refreshTraining(a.buildingId);
        else this.ctx.hud.toast(r.message, 'error');
        break;
      }
      case 'hover':
        if (a.screen) this.ctx.hud.setInputMode('mouse');
        this.buildMode.setCursor(a.screen ? this.coords.screenToTileUnbounded(a.screen) : null);
        this.selection.hover(a.screen ? this.coords.screenToTile(a.screen) : null);
        break;
      case 'pan':
        this.camera.pan(a.dx, a.dy);
        break;
      case 'zoom':
        this.camera.zoomBy(a.factor, a.screen);
        break;
      case 'pinch':
        this.ctx.hud.setInputMode('touch');
        this.camera.pinch(a.factor, a.from, a.to);
        break;
      case 'placementDrag':
        if (this.buildMode.isActive) this.buildMode.pinCursor(this.coords.screenToTileUnbounded(a.screen));
        break;
      case 'confirmBuild':
        if (!this.buildMode.isActive) break;
        if (!this.buildMode.state.preview) this.ctx.hud.toast('Tap the map to choose a spot first.', 'info');
        else this.report(this.buildMode.place(), true);
        break;
      case 'resetView':
        this.resetView();
        break;
      case 'clearSelection':
        this.selection.clear();
        break;
      case 'cancel':
        if (this.buildMode.isActive) this.buildMode.cancel();
        else if (this.attackMoveArmed) this.setAttackMove(false);
        else if (this.selectedUnits.size || this.inspectedEnemy !== null) this.setUnitSelection([]);
        else this.selection.clear();
        break;
      case 'selectBuild':
        this.startBuild(a.building);
        break;
      case 'buildHotkey': {
        const def = PLAYER_BUILDABLE.find((d) => d.hotkey === a.key);
        if (def) this.startBuild(def.id);
        break;
      }
      case 'demolish': {
        const b = this.ctx.sim.buildings.get(a.id);
        const r = this.ctx.sink.submit({ type: 'demolishBuilding', playerId: this.ctx.localPlayer, buildingId: a.id });
        if (r.ok) this.ctx.hud.toast(`${b ? buildingDef(b.type).name : 'Building'} demolished.`, 'info');
        else this.ctx.hud.toast(r.message, 'error');
        break;
      }
      case 'toggleColony':
        this.setColonyOpen(!this.colonyOpen);
        break;
      case 'playAgain':
        window.location.reload();
        break;
      case 'upgradeBuilding': {
        const r = this.ctx.sink.submit({ type: 'upgradeBuilding', playerId: this.ctx.localPlayer, buildingId: a.id });
        if (!r.ok) this.ctx.hud.toast(r.message, 'error');
        break;
      }
      case 'adjustWorkers': {
        const now = this.ctx.sim.colony.workersAt(a.id);
        const r = this.ctx.sink.submit({ type: 'setWorkers', playerId: this.ctx.localPlayer, buildingId: a.id, workers: now + a.delta });
        if (!r.ok) this.ctx.hud.toast(r.message, 'error');
        break;
      }
      case 'toggleGrid':
        this.mapRenderer.setGridVisible(!this.mapRenderer.gridVisible);
        this.ctx.hud.setGrid(this.mapRenderer.gridVisible);
        break;
      case 'togglePassability':
        this.mapRenderer.setPassabilityVisible(!this.mapRenderer.passabilityVisible);
        this.ctx.hud.setPassability(this.mapRenderer.passabilityVisible);
        break;
      case 'toggleDebug':
        this.ctx.hud.setDebug(this.ctx.hud.debugVisible ? null : this.debugText());
        break;
    }
  }

  private startBuild(type: Parameters<BuildMode['start']>[0]): void {
    const wasPinned = this.buildMode.state.pinned;
    this.buildMode.toggle(type);
    if (!this.buildMode.isActive) return;
    const p = this.inputs.pointerPosition;
    if (p) {
      this.buildMode.setCursor(this.coords.screenToTileUnbounded(p));
    } else if (!wasPinned) {
      // No mouse over the map (touch, or the button was pressed): start the preview
      // in the middle of the view so there is something to drag or confirm.
      const v = this.camera.view();
      this.buildMode.pinNearestValid(this.coords.screenToTileUnbounded({ x: v.viewWidth / 2, y: v.viewHeight / 2 }));
    }
  }

  private onEconomy(e: EconomyEvent): void {
    const local = this.ctx.localPlayer;
    switch (e.type) {
      case 'changed':
        if (e.playerId === local) {
          this.refreshResources();
          this.buildMode.revalidate();
        }
        break;
      case 'produced':
        if (e.amount > 0) this.buildingRenderer.floatText(e.buildingId, `+${e.amount} ${e.resource}`, FLOAT_COLOR[e.resource]);
        break;
      case 'stalled':
        if (e.stalled && e.playerId === local) this.buildingRenderer.floatText(e.buildingId, 'Storage full', '#f0b43c');
        break;
      case 'shortage':
        if (e.playerId === local && e.shortage) this.ctx.hud.toast('Food has run out — your survivors are going hungry. Build a Farm.', 'error');
        if (e.playerId === local) this.refreshResources();
        break;
      case 'lost':
        if (e.playerId === local) this.ctx.hud.toast(`Storage shrank — lost ${formatCost(e.amounts)}.`, 'error');
        break;
      default:
        break;
    }
  }

  // ---------------------------------------------------------------- units

  /**
   * A tap / click (or right-click) on the map with respect to units. Returns
   * true when it was consumed (a unit was selected or an order given).
   *  - on your own unit → select it
   *  - with units selected: on an enemy unit / building → attack; on open
   *    ground → move; on your own building → (primary only) select it instead
   *  - on an enemy unit with nothing selected → inspect it
   */
  private unitTap(screen: { x: number; y: number }, touch: boolean, commandOnly: boolean): boolean {
    const sim = this.ctx.sim;
    const me = this.ctx.localPlayer;
    const tol = (touch ? 14 : 5) / this.camera.zoom;
    const hit = this.unitRenderer.unitAt(this.coords.screenToWorld(screen), tol);
    if (hit && hit.ownerId === me) {
      if (commandOnly) return true;
      this.setUnitSelection([hit.id]);
      return true;
    }
    const mine = [...this.selectedUnits];
    if (hit) {
      if (mine.length) {
        this.unitCommand({ type: 'attack', playerId: me, unitIds: mine, target: { kind: 'unit', id: hit.id } });
        this.unitRenderer.marker(hit.x, hit.y, 0xef5a4c);
      } else if (!commandOnly) {
        this.inspectedEnemy = hit.id;
        this.selection.clear();
        this.ctx.hud.setUnits(this.unitsView());
      }
      return true;
    }
    if (!mine.length) return false;
    const tile = this.coords.screenToTile(screen);
    if (!tile) return true; // off the map: ignore while commanding
    const b = sim.buildings.at(tile.x, tile.y);
    if (b && b.ownerId !== me && b.ownerId !== 'neutral') {
      this.unitCommand({ type: 'attack', playerId: me, unitIds: mine, target: { kind: 'building', id: b.id } });
      this.unitRenderer.marker(tile.x, tile.y, 0xef5a4c);
      return true;
    }
    // Own damaged building with Engineers selected → repair it.
    if (b && b.ownerId === me && b.state === 'complete' && b.health < b.maxHealth && mine.some((id) => !!unitDef(sim.units.get(id)?.type ?? 'ranger').repairRate)) {
      const r = this.ctx.sink.submit({ type: 'repairBuilding', playerId: me, unitIds: mine, buildingId: b.id });
      if (r.ok) this.unitRenderer.marker(tile.x, tile.y, 0x6fb8ff);
      else this.ctx.hud.toast(r.message, 'error');
      return true;
    }
    if (b && !commandOnly) return false; // own building: select it (units are deselected by the selection listener)
    this.unitCommand({ type: 'moveUnits', playerId: me, unitIds: mine, target: tile });
    this.unitRenderer.marker(tile.x, tile.y, 0x7fcf6a);
    return true;
  }

  /** Armed attack-move tap: enemy → attack it; own unit → select it; anywhere else → attack-move there. */
  private attackMoveTap(screen: { x: number; y: number }, touch: boolean): void {
    const me = this.ctx.localPlayer;
    const tol = (touch ? 14 : 5) / this.camera.zoom;
    const hit = this.unitRenderer.unitAt(this.coords.screenToWorld(screen), tol);
    this.setAttackMove(false);
    if (hit) {
      this.unitTap(screen, touch, false);
      return;
    }
    const tile = this.coords.screenToTile(screen);
    if (!tile) return;
    const b = this.ctx.sim.buildings.at(tile.x, tile.y);
    if (b && b.ownerId !== me && b.ownerId !== 'neutral') {
      this.unitTap(screen, touch, true);
      return;
    }
    this.unitCommand({ type: 'attackMove', playerId: me, unitIds: [...this.selectedUnits], target: tile });
    this.unitRenderer.marker(tile.x, tile.y, 0xf08a3c);
  }

  private setAttackMove(on: boolean): void {
    if (this.attackMoveArmed === on) return;
    this.attackMoveArmed = on;
    if (this.selectedUnits.size) this.ctx.hud.setUnits(this.unitsView());
  }

  private onMatch(e: MatchEvent): void {
    const me = this.ctx.localPlayer;
    if (e.type === 'defeated') {
      const p = this.ctx.sim.player(e.playerId);
      if (e.playerId !== me) this.ctx.hud.toast(`${p?.name ?? e.playerId} is out — ${e.reason.toLowerCase()}.`, 'info');
      return;
    }
    const r = e.result;
    const won = r.winners.includes(me);
    const st = this.ctx.sim.match.statsOf(me);
    const mins = Math.floor(r.at / 60);
    const secs = Math.floor(r.at % 60);
    this.setUnitSelection([]);
    this.ctx.hud.showResult({
      title: won ? 'Victory' : r.winners.length ? 'Defeat' : 'Colony fallen',
      outcome: won ? 'victory' : r.winners.length ? 'defeat' : 'defeat',
      reason: r.reason,
      lines: [
        `${this.ctx.sim.mode.name} · ${mins}:${String(secs).padStart(2, '0')}`,
        `Enemies killed ${st.kills} · units lost ${st.unitsLost} · buildings lost ${st.buildingsLost}`,
      ],
    });
  }

  private lastPowerAlert = -Infinity;
  private readonly repairFx = new Map<number, number>();

  private onDefence(e: DefenceEvent): void {
    if (e.type === 'turretFired') {
      const b = this.ctx.sim.buildings.get(e.buildingId);
      if (!b) return;
      const fp = buildingDef(b.type).footprint;
      const w = this.iso.gridToWorld(b.origin.x + (fp.width - 1) / 2, b.origin.y + (fp.height - 1) / 2);
      const bounds = this.buildingRenderer.spriteBounds(b.id);
      this.flashes.dot(w.x, bounds ? bounds.top + bounds.height * 0.18 : w.y - 40, 4, 0xffe08a, 90);
      return;
    }
    if (e.playerId !== this.ctx.localPlayer) return;
    const p = this.ctx.sim.defence.power(e.playerId);
    if (p.demand > p.supply && this.time.now - this.lastPowerAlert > 10_000) {
      this.lastPowerAlert = this.time.now;
      this.ctx.hud.toast(`Not enough power (${p.supply} / ${p.demand}) — some Gun Towers are off. Build a Generator.`, 'error');
    }
    if (this.selection.state.building !== null) this.selection.refresh();
    if (this.colonyOpen) this.refreshColony();
  }

  private onInfected(e: InfectedEvent): void {
    const me = this.ctx.localPlayer;
    const now = this.time.now;
    switch (e.type) {
      case 'wave': {
        if (e.target === me) this.ctx.hud.toast(`A horde of ${e.count} infected is coming for your colony!`, 'error');
        else this.ctx.hud.toast(`A horde of ${e.count} infected is heading for a rival colony.`, 'info');
        const w = this.iso.gridToWorld(e.from.x, e.from.y);
        this.flashes.ring(w.x, w.y, 40, 0xb5c23a, 900);
        break;
      }
      case 'infected':
        this.unitRenderer.setInfected(e.unitId, true);
        if (e.ownerId === me && now - this.lastInfectAlert > 8000) {
          this.lastInfectAlert = now;
          const u = this.ctx.sim.units.get(e.unitId);
          this.ctx.hud.toast(`Your ${u ? unitDef(u.type).name : 'unit'} is infected — it will turn if it dies.`, 'error');
        }
        break;
      case 'rose': {
        const w = this.iso.gridToWorld(e.at.x, e.at.y);
        this.flashes.ring(w.x, w.y - 8, 18, 0xb5c23a, 600);
        this.flashes.dot(w.x, w.y - 12, 10, 0x9fb23a, 400);
        if (e.formerOwner === me) this.ctx.hud.toast('A fallen survivor has risen as a Walker.', 'error');
        break;
      }
      default:
        break;
    }
    if (this.selectedUnits.size || this.inspectedEnemy !== null) this.ctx.hud.setUnits(this.unitsView());
  }

  /** "Under attack" alert for the local player, at most every 12 s. */
  private underAttack(): void {
    const now = this.time.now;
    if (now - this.lastAlert < 12_000) return;
    this.lastAlert = now;
    this.ctx.hud.toast('Your colony is under attack!', 'error');
  }

  private unitCommand(cmd: Parameters<GameContext['sink']['submit']>[0]): void {
    const r = this.ctx.sink.submit(cmd);
    if (!r.ok) this.ctx.hud.toast(r.message, 'error');
  }

  private boxSelect(from: { x: number; y: number }, to: { x: number; y: number }, done: boolean): void {
    const x = Math.min(from.x, to.x);
    const y = Math.min(from.y, to.y);
    this.ctx.hud.setSelectBox(done ? null : { x, y, w: Math.abs(to.x - from.x), h: Math.abs(to.y - from.y) });
    if (!done) return;
    const a = this.coords.screenToWorld(from);
    const b = this.coords.screenToWorld(to);
    const mine = this.unitRenderer.unitsInRect(a.x, a.y, b.x, b.y).filter((u) => u.ownerId === this.ctx.localPlayer);
    this.setUnitSelection(mine.map((u) => u.id));
  }

  private setUnitSelection(ids: readonly number[], keepTile = false): void {
    this.selectedUnits = new Set(ids);
    if (!ids.length) this.attackMoveArmed = false;
    this.inspectedEnemy = null;
    this.unitRenderer.setSelected(this.selectedUnits);
    if (ids.length) {
      if (this.colonyOpen) this.setColonyOpen(false);
      if (!keepTile) this.selection.clear();
      this.ctx.hud.setUnits(this.unitsView());
    } else {
      this.ctx.hud.setUnits(null);
      const s = this.selection.state;
      if (s.selected) this.ctx.hud.setInspection(this.inspectTile(s.selected), s.building ? this.inspectBuilding(s.building) : null);
    }
  }

  private onUnits(e: UnitEvent): void {
    switch (e.type) {
      case 'died':
        if (this.selectedUnits.has(e.unit.id)) this.setUnitSelection([...this.selectedUnits].filter((i) => i !== e.unit.id));
        if (this.inspectedEnemy === e.unit.id) this.setUnitSelection([]);
        this.refreshArmy();
        break;
      case 'damaged':
        if (e.unit.ownerId === this.ctx.localPlayer) this.underAttack();
        break;
      case 'repairing': {
        const now = this.time.now;
        if ((this.repairFx.get(e.buildingId) ?? 0) > now) break;
        this.repairFx.set(e.buildingId, now + 400);
        const b = this.ctx.sim.buildings.get(e.buildingId);
        if (!b) break;
        const fp = buildingDef(b.type).footprint;
        const w = this.iso.gridToWorld(b.origin.x + (fp.width - 1) / 2, b.origin.y + (fp.height - 1) / 2);
        this.flashes.dot(w.x + ((now / 37) % 12) - 6, w.y - 14, 3, 0x9fd3ff, 200);
        break;
      }
      case 'repairStopped':
        if (e.unit.ownerId === this.ctx.localPlayer && e.reason === 'no_scrap') this.ctx.hud.toast('Repairs stopped — out of scrap.', 'error');
        break;
      case 'spawned':
        if (e.unit.ownerId === this.ctx.localPlayer) {
          this.refreshArmy();
          const def = unitDef(e.unit.type);
          if (e.from) this.buildingRenderer.floatText(e.from, `${def.name} ready`, '#9fd3ff');
        }
        break;
      case 'queue':
        this.refreshTraining(e.buildingId);
        break;
      default:
        break;
    }
  }

  private refreshArmy(): void {
    this.ctx.hud.setArmy(this.ctx.sim.units.ownedBy(this.ctx.localPlayer).length, this.boxMode);
  }

  private refreshTraining(buildingId: number): void {
    const t = this.trainingView(buildingId);
    if (t) this.ctx.hud.updateTraining(buildingId, t);
  }

  private trainingView(buildingId: number): TrainingView | null {
    const sim = this.ctx.sim;
    const b = sim.buildings.get(buildingId);
    if (!b || b.ownerId !== this.ctx.localPlayer) return null;
    const trains = buildingDef(b.type).trains;
    if (!trains?.length) return null;
    const ledger = sim.economy.ledger(this.ctx.localPlayer);
    return {
      ready: b.state === 'complete',
      options: trains.map((u: UnitTypeId) => {
        const d = unitDef(u);
        return { unit: u, name: d.name, cost: d.cost, seconds: d.trainSeconds, affordable: ledger.canAfford(d.cost).ok && sim.colony.population(this.ctx.localPlayer) >= d.survivors };
      }),
      queue: sim.trainingQueue(buildingId).map((j) => ({ name: unitDef(j.unit).name, progress: j.progress / unitDef(j.unit).trainSeconds })),
      limit: TRAIN_QUEUE_LIMIT,
    };
  }

  private unitsView(): UnitsView {
    const sim = this.ctx.sim;
    const ids = this.inspectedEnemy !== null ? [this.inspectedEnemy] : [...this.selectedUnits];
    const units = ids.map((i) => sim.units.get(i)).filter((u): u is Unit => !!u);
    const counts = new Map<string, number>();
    for (const u of units) counts.set(unitDef(u.type).name, (counts.get(unitDef(u.type).name) ?? 0) + 1);
    const view: UnitsView = {
      count: units.length,
      groups: [...counts.entries()].map(([name, count]) => ({ name, count })),
      health: units.reduce((n, u) => n + u.health, 0),
      maxHealth: units.reduce((n, u) => n + u.maxHealth, 0),
      commandable: this.inspectedEnemy === null && units.length > 0,
      stance: units.every((u) => u.stance === units[0]?.stance) ? (units[0]?.stance ?? 'aggressive') : 'mixed',
      attackMoveArmed: this.attackMoveArmed,
    };
    if (units.length !== 1) return view;
    const u = units[0]!;
    const d = unitDef(u.type);
    const owner = typeof u.ownerId === 'string' && u.ownerId.startsWith('p') ? sim.player(u.ownerId as PlayerId) : null;
    const order = orderText(u, sim);
    return {
      ...view,
      single: {
        name: d.name,
        role: d.role,
        owner: owner ? `${owner.name} (${u.ownerId.toUpperCase()})` : u.ownerId,
        ownerCss: (OWNER_COLORS[u.ownerId] ?? OWNER_COLORS.neutral!).css,
        isYou: u.ownerId === this.ctx.localPlayer,
        ...infectionStatus(u, sim),
        stats: `${d.infected ? 'Bite' : d.damageType === 'bullet' ? 'Rifle' : 'Melee'} ${d.damage} every ${d.cooldown} s · range ${d.range} · sight ${d.sight} · armour ${d.armor} (${d.armorClass}) · speed ${d.speed} tiles/s`,
        order,
      },
    };
  }

  private onColony(e: ColonyEvent): void {
    if (e.playerId !== this.ctx.localPlayer) return;
    switch (e.type) {
      case 'changed':
        this.refreshColony();
        this.refreshSelectedBuilding();
        break;
      case 'joined': {
        const cc = this.ctx.sim.buildings.ownedBy(e.playerId).find((b) => b.type === 'command_center');
        if (cc) this.buildingRenderer.floatText(cc.id, `+${e.count} survivor`, '#c9e6a1');
        break;
      }
      case 'left':
        if (e.reason === 'starvation') this.ctx.hud.toast('A survivor left the colony — there is not enough food.', 'error');
        else if (e.reason === 'homeless') this.ctx.hud.toast(`${e.count} survivor${e.count > 1 ? 's' : ''} left — no shelter. Build Survivor Housing.`, 'error');
        break;
    }
  }

  private setColonyOpen(open: boolean): void {
    this.colonyOpen = open;
    if (open) this.selection.clear();
    this.refreshColony();
  }

  /** Re-render the selected building's card (worker numbers and production speed change with the colony). */
  private refreshSelectedBuilding(): void {
    const s = this.selection.state;
    if (s.building !== null && s.selected) this.ctx.hud.setInspection(this.inspectTile(s.selected), this.inspectBuilding(s.building));
  }

  private refreshColony(): void {
    const sim = this.ctx.sim;
    const p = this.ctx.localPlayer;
    const c = sim.colony.summary(p);
    this.ctx.hud.setPopulation({ population: c.population, capacity: c.capacity, starving: sim.economy.hasShortage(p) });
    this.ctx.hud.setColony(this.colonyOpen ? this.colonyView() : null);
  }

  private colonyView(): ColonyView {
    const sim = this.ctx.sim;
    const p = this.ctx.localPlayer;
    const c = sim.colony.summary(p);
    let foodIn = 0;
    const workplaces: { id: number; name: string; workers: number; slots: number }[] = [];
    for (const b of sim.buildings.ownedBy(p)) {
      const def = buildingDef(b.type);
      if (!def.workerSlots || b.state !== 'complete') continue;
      workplaces.push({ id: b.id, name: def.name, workers: sim.colony.workersAt(b.id), slots: def.workerSlots });
      if (def.production?.resource === 'food') foodIn += def.production.amount * (60 / def.production.intervalSeconds) * sim.colony.workRatio(b.id);
    }
    const foodOut = c.foodNeeded * (60 / ECONOMY.upkeepIntervalSeconds);
    const canGrow = sim.canGrow(p);
    const next = sim.colony.nextArrival(p, canGrow);
    const starving = sim.economy.hasShortage(p);
    const status: ColonyStatus =
      c.population === 0 ? 'Empty' : starving ? 'Starving' : foodIn - foodOut < 0 ? 'Hungry' : c.population >= c.capacity ? 'Housing full' : canGrow ? 'Growing' : 'Stable';
    const growth =
      c.population >= c.capacity
        ? 'Housing is full — build Survivor Housing so more survivors can join.'
        : next !== null
          ? `Next survivor arrives in ${Math.ceil(next)} s.`
          : 'Newcomers wait for food: keep at least 10 food and avoid shortages.';
    const zombies = sim.infected.count;
    const toWave = sim.infected.secondsToWave;
    const wave = toWave === null ? 'hordes are off' : `next horde in ${Math.floor(toWave / 60)}:${String(Math.floor(toWave % 60)).padStart(2, '0')}`;
    const threat = `${zombies} infected on the map · ${wave}.`;
    const infectedUnits = sim.units.ownedBy(p).filter((u) => sim.infected.isInfected(u.id)).length;
    const pw = sim.defence.power(p);
    const power = pw.supply || pw.demand ? `Power ${pw.used} / ${pw.supply} used${pw.demand > pw.supply ? ` — ${pw.demand - pw.supply} short, some Gun Towers are off` : ''}.` : undefined;
    return { status, population: c.population, capacity: c.capacity, working: c.assigned, idle: c.idle, foodIn, foodOut, growth, workplaces, threat, infectedUnits, power };
  }

  private refreshResources(): void {
    const sim = this.ctx.sim;
    const p = this.ctx.localPlayer;
    const l = sim.economy.ledger(p);
    const amounts = { wood: l.get('wood'), scrap: l.get('scrap'), food: l.get('food') };
    const capacity = { wood: l.capacity('wood'), scrap: l.capacity('scrap'), food: l.capacity('food') };
    this.ctx.hud.setResources({ amounts, capacity, shortage: sim.economy.hasShortage(p) });
    const affordable = new Set<BuildingTypeId>(PLAYER_BUILDABLE.filter((d) => l.canAfford(d.cost).ok).map((d) => d.id));
    this.ctx.hud.setAffordable(affordable);
  }

  /** Live production line for the inspector. */
  private economyStatus(id: number): string {
    const b = this.ctx.sim.buildings.get(id);
    if (!b || !buildingDef(b.type).production) return '';
    if (b.state !== 'complete') return 'Starts producing when construction finishes.';
    const st = this.ctx.sim.economy.productionStatus(id);
    if (!st) return '';
    if (st.stalled) return 'Storage full — paused until there is room (build a Warehouse).';
    if (st.speed <= 0) return 'No workers — nothing is produced. Add workers below.';
    return `Next batch in ${Math.ceil(st.remaining)} s`;
  }

  /**
   * On phones the inspector covers part of the map (a bottom sheet in portrait,
   * a side panel in landscape). If the tapped tile ended up underneath it, pan
   * so the tile sits in the middle of the free area.
   */
  private keepClearOfPanels(tile: GridPoint): void {
    const panel = this.ctx.hud.inspectorRect();
    if (!panel) return;
    const p = this.coords.tileToScreen(tile);
    const m = 24;
    if (p.x < panel.left - m || p.x > panel.right + m || p.y < panel.top - m || p.y > panel.bottom + m) return;
    const v = this.camera.view();
    const top = this.ctx.hud.topBarBottom();
    const isSheet = panel.top > v.viewHeight / 2 && panel.width > v.viewWidth * 0.6;
    const target = isSheet ? { x: v.viewWidth / 2, y: (top + panel.top) / 2 } : { x: panel.left / 2, y: (top + v.viewHeight) / 2 };
    this.camera.pan(target.x - p.x, target.y - p.y);
  }

  private report(r: CommandResult, placing: boolean): void {
    if (r.ok) {
      const b = this.ctx.sim.buildings.get(r.buildingId);
      if (placing && b) this.ctx.hud.toast(`${buildingDef(b.type).name} placed — construction started.`, 'ok');
    } else {
      this.ctx.hud.toast(r.message, 'error');
    }
  }

  private areaOf(id: number): MarkArea | null {
    const b = this.ctx.sim.buildings.get(id);
    return b ? { origin: b.origin, footprint: buildingDef(b.type).footprint } : null;
  }

  /** World-space rectangle currently on screen. */
  private viewRect(): { left: number; top: number; right: number; bottom: number } {
    const v = this.camera.view();
    const a = this.coords.screenToWorld({ x: 0, y: 0 });
    const b = this.coords.screenToWorld({ x: v.viewWidth, y: v.viewHeight });
    return { left: a.x, top: a.y, right: b.x, bottom: b.y };
  }

  private refreshHover(): void {
    const p = this.inputs.pointerPosition;
    this.buildMode.setCursor(p ? this.coords.screenToTileUnbounded(p) : null);
    this.selection.hover(p ? this.coords.screenToTile(p) : null);
  }

  private resetView(): void {
    const local = this.ctx.sim.player(this.ctx.localPlayer);
    const slot = local ? this.ctx.map.playerSlots[local.slot] : null;
    const cam = this.ctx.config.camera;
    this.camera.setZoomImmediate(this.ctx.hud.inputMode === 'touch' ? cam.touchDefaultZoom : cam.defaultZoom);
    if (slot) this.camera.centerOnGrid(slot.x, slot.y);
    else this.camera.centerOnGrid((this.ctx.map.width - 1) / 2, (this.ctx.map.height - 1) / 2);
  }

  private inspectTile(t: GridPoint): TileInspection | null {
    const md = this.ctx.map.tileMetadata(t.x, t.y);
    return md ? { ...md, swatch: TERRAIN_PALETTE[md.terrain.id].base } : null;
  }

  private inspectBuilding(id: number): BuildingInspection | null {
    const b = this.ctx.sim.buildings.get(id);
    if (!b) return null;
    const def = buildingDef(b.type);
    const owner = typeof b.ownerId === 'string' && b.ownerId.startsWith('p') ? this.ctx.sim.player(b.ownerId as PlayerId) : null;
    const lv = statsAt(b.type, b.level);
    const mine = b.ownerId === this.ctx.localPlayer;
    const defence: string[] = [];
    if (lv.armor) defence.push(`Armour ${lv.armor} (structure)`);
    if (lv.turret) defence.push(`Shoots enemies up to ${lv.turret.range} tiles away · ${lv.turret.damage} rifle damage every ${lv.turret.cooldown} s`);
    if (def.powerUse) defence.push(`Needs ${def.powerUse} power — ${b.state !== 'complete' ? 'when complete' : this.ctx.sim.defence.isPowered(b.id) ? 'powered' : 'NO POWER: build a Generator'}`);
    if (lv.power) {
      const pw = isPlayerIdLike(b.ownerId) ? this.ctx.sim.defence.power(b.ownerId as PlayerId) : null;
      defence.push(`Supplies ${lv.power} power${pw ? ` (colony: ${pw.used} / ${pw.supply} used)` : ''}`);
    }
    if (def.ownerPassable) defence.push('Your units walk through; enemies have to break it');
    const up = def.upgrade && mine && b.state === 'complete'
      ? {
          name: def.upgrade.name,
          cost: formatCost(def.upgrade.cost),
          seconds: def.upgrade.seconds,
          affordable: this.ctx.sim.economy.ledger(this.ctx.localPlayer).canAfford(def.upgrade.cost).ok,
          progress: b.upgradeProgress === null ? null : b.upgradeProgress / def.upgrade.seconds,
          done: b.level >= 2,
        }
      : undefined;
    const repairHint =
      mine && b.state === 'complete' && b.health < b.maxHealth
        ? `Damaged — select Engineers and ${this.ctx.hud.inputMode === 'touch' ? 'tap' : 'click'} it to repair (1 scrap per ${Math.round(1 / REPAIR.scrapPerHealth)} health). Idle Engineers nearby repair it by themselves.`
        : undefined;
    return {
      id: b.id,
      name: lv.name,
      defence,
      upgrade: up,
      repairHint,
      category: def.category,
      purpose: def.purpose,
      state: b.state,
      progress: b.progress,
      health: b.health,
      maxHealth: b.maxHealth,
      origin: b.origin,
      footprint: def.footprint,
      operationalFromPhase: def.operationalFromPhase,
      pendingFunctionPhase: def.operationalFromPhase > CURRENT_PHASE ? def.operationalFromPhase : undefined,
      training: this.trainingView(b.id) ?? undefined,
      cost: formatCost(def.cost),
      workers: def.workerSlots
        ? {
            current: this.ctx.sim.colony.workersAt(b.id),
            slots: def.workerSlots,
            editable: b.ownerId === this.ctx.localPlayer && b.state === 'complete',
            canAdd: this.ctx.sim.colony.idle(this.ctx.localPlayer) > 0,
          }
        : undefined,
      economy: {
        production: def.production ? productionText(def.production, def.workerSlots ?? 0, b.state === 'complete' ? this.ctx.sim.colony.workersAt(b.id) : null) : undefined,
        housing: lv.housing ? `${lv.housing} survivors${b.state === 'complete' ? '' : ' (when complete)'}` : undefined,
        storage: lv.storage ? `+${formatCost(lv.storage)}${b.state === 'complete' ? '' : ' (when complete)'}` : undefined,
        upkeep: undefined,
        refund: b.ownerId === this.ctx.localPlayer && def.demolishable ? refundText(this.ctx.sim.refundFor(b.id)) : undefined,
      },
      economyStatus: this.economyStatus(b.id),
      demolishable: def.demolishable,
      preplaced: b.preplaced,
      owner: {
        id: b.ownerId,
        name: owner?.name ?? b.ownerId,
        css: (OWNER_COLORS[b.ownerId] ?? OWNER_COLORS.neutral!).css,
        isYou: b.ownerId === this.ctx.localPlayer,
        hasController: owner ? owner.control !== 'none' : false,
      },
    };
  }

  private onResize(): void {
    this.camera.handleResize();
  }

  private debugText(): string {
    const s = this.selection.state;
    const v = this.camera.view();
    const p = this.inputs.pointerPosition;
    const w = p ? this.coords.screenToWorld(p) : null;
    return [
      `fps      ${this.game.loop.actualFps.toFixed(0)}`,
      `renderer ${this.game.renderer.type === Phaser.WEBGL ? 'WebGL' : 'Canvas'}`,
      `sim tick ${this.ctx.sim.tick} @ ${this.ctx.config.simulation.ticksPerSecond}/s`,
      `zoom     ${v.zoom.toFixed(3)}${this.camera.isZooming ? ` → ${this.camera.targetZoom.toFixed(3)}` : ''}`,
      `view     ${Math.round(v.viewWidth)}×${Math.round(v.viewHeight)}`,
      `screen   ${p ? `${p.x.toFixed(0)}, ${p.y.toFixed(0)}` : '—'}`,
      `world    ${w ? `${w.x.toFixed(1)}, ${w.y.toFixed(1)}` : '—'}`,
      `tile     ${s.hovered ? `${s.hovered.x}, ${s.hovered.y}` : '—'}`,
      `buildings ${this.ctx.sim.buildings.count}`,
      `units    ${this.ctx.sim.units.count} · shots ${this.ctx.sim.units.projectiles.count} · infected ${this.ctx.sim.infected.count}`,
      `objects  ${this.mapRenderer.objectCount}`,
      `chunks   ${this.mapRenderer.cullingStats.visible}/${this.mapRenderer.cullingStats.total} drawn`,
      `map      ${this.ctx.map.width}×${this.ctx.map.height} · tile ${this.iso.tileWidth}×${this.iso.tileHeight} · seed ${this.ctx.config.map.seed}`,
    ].join('\n');
  }

  private installDebugApi(): void {
    const scene = this;
    const input = this.input;
    const sim = this.ctx.sim;
    window.__AFTER_THE_FALL__ = {
      ready: true,
      renderer: this.game.renderer.type === Phaser.WEBGL ? 'webgl' : 'canvas',
      state() {
        const v = scene.camera.view();
        const s = scene.selection.state;
        return {
          zoom: v.zoom,
          targetZoom: scene.camera.targetZoom,
          zooming: scene.camera.isZooming,
          scrollX: v.scrollX,
          scrollY: v.scrollY,
          viewWidth: v.viewWidth,
          viewHeight: v.viewHeight,
          selected: s.selected,
          hovered: s.hovered,
          selectedBuilding: s.building,
          gridVisible: scene.mapRenderer.gridVisible,
          passabilityVisible: scene.mapRenderer.passabilityVisible,
          ticks: scene.clock.ticks,
          objects: scene.mapRenderer.objectCount,
          chunks: scene.mapRenderer.cullingStats,
          mapSize: { width: scene.ctx.map.width, height: scene.ctx.map.height },
          tileSize: { width: scene.iso.tileWidth, height: scene.iso.tileHeight },
          starts: sim.allPlayers().map((p) => ({ owner: p.id, tile: scene.ctx.map.playerSlots[p.slot]! })),
          localPlayer: scene.ctx.localPlayer,
          inputMode: scene.ctx.hud.inputMode,
          pinching: scene.inputs.isPinching,
          buildRadius: sim.buildings.buildRadius,
        };
      },
      diagnostics() {
        const E = Phaser.Input.Events;
        return {
          liveGames: scene.ctx.liveGames(),
          busListeners: scene.ctx.bus.listenerCount,
          cameraListeners: scene.camera.listenerCount,
          pointerDown: input.listenerCount(E.POINTER_DOWN),
          pointerMove: input.listenerCount(E.POINTER_MOVE),
          pointerUp: input.listenerCount(E.POINTER_UP),
          wheel: input.listenerCount(E.POINTER_WHEEL),
          keyDown: scene.inputs.keyboardListeners,
          resize: scene.scale.listeners(Phaser.Scale.Events.RESIZE).filter((fn) => fn === scene.onResize).length,
          sceneUpdates: scene.updates,
          loopFrames: scene.game.loop.frame,
        };
      },
      buildings: () => sim.buildings.all(),
      projectiles: () => sim.units.projectiles.all().map((p) => ({ id: p.id, x: p.x, y: p.y, ownerId: p.ownerId })),
      damageState: (id: number) => scene.buildingRenderer.damageStateOf(id),
      attackMoveArmed: () => scene.attackMoveArmed,
      toasts: () => [...scene.ctx.hud.recentToasts],
      net: () => {
        const n = scene.ctx.net;
        return n ? { room: n.room, me: n.me, tick: n.localTick, backlog: n.backlog, desyncs: n.desyncs, connected: !!n.ws && n.ws.readyState === WebSocket.OPEN } : null;
      },
      match: () => {
        const m = sim.match;
        const r = m.result;
        return {
          mode: sim.mode.id,
          result: r ? { winners: [...r.winners], reason: r.reason } : null,
          status: Object.fromEntries(sim.allPlayers().map((p) => [p.id, m.statusOf(p.id)])),
          timeLeft: m.timeLeft,
        };
      },
      infection: () => {
        const snap = sim.infected.snapshot();
        return { count: sim.infected.count, infected: snap.infected, exposure: snap.exposure, secondsToWave: sim.infected.secondsToWave, pendingRises: sim.infected.pendingRises };
      },
      fx: () => ({ flashes: scene.flashes.count, damageNumbers: scene.damageNumbers.count, tracers: scene.unitRenderer.shotCount, bursts: scene.buildingRenderer.burstCount }),
      resources(playerId) {
        const p = (playerId ?? scene.ctx.localPlayer) as PlayerId;
        const l = sim.economy.ledger(p);
        return { ...l.toJSON(), shortage: sim.economy.hasShortage(p) };
      },
      economy: () => sim.economy.snapshot(),
      colony: (playerId) => sim.colony.summary((playerId ?? scene.ctx.localPlayer) as PlayerId),
      workersAt: (id) => sim.colony.workersAt(id),
      units: () => sim.units.all(),
      selectedUnits: () => [...scene.selectedUnits],
      boxMode: () => scene.boxMode,
      unitScreen: (id) => {
        const w = scene.unitRenderer.drawnAt(id);
        return w ? scene.coords.worldToScreen(w) : null;
      },
      trainingQueue: (id) => sim.trainingQueue(id).map((j) => ({ unit: j.unit, progress: j.progress })),
      productionStatus: (id) => sim.economy.productionStatus(id),
      floaters: () => scene.buildingRenderer.floaterCount,
      buildMode() {
        const b = scene.buildMode.state;
        const p = b.preview;
        return {
          active: b.active,
          pinned: b.pinned,
          preview: p ? { ok: p.ok, origin: p.origin, failures: [...p.failures], message: p.message, tiles: p.tiles.map((t) => ({ x: t.x, y: t.y, ok: t.ok })) } : null,
        };
      },
      buildingSpriteScreenBounds: (id) => scene.rectToScreen(scene.buildingRenderer.spriteBounds(id)),
      previewScreenBounds: () => scene.rectToScreen(scene.buildingRenderer.previewBounds()),
      checkPlacement(type, x, y) {
        if (!isBuildingTypeId(type)) return { ok: false, failures: ['invalid_payload'], message: 'Unknown building type.' };
        const r = sim.validatePlacement(scene.ctx.localPlayer, type, { x, y });
        return { ok: r.ok, failures: [...r.failures], message: r.message };
      },
      centerOn: (x, y) => scene.camera.centerOnGrid(x, y),
      buildingAnchorScreen(id) {
        const w = scene.buildingRenderer.spriteAnchor(id);
        return w ? scene.coords.worldToScreen(w) : null;
      },
      tileToScreen: (x, y) => scene.coords.tileToScreen({ x, y }),
      screenToTile: (x, y) => scene.coords.screenToTileUnbounded({ x, y }),
      screenToWorld: (x, y) => scene.coords.screenToWorld({ x, y }),
      worldToScreen: (x, y) => scene.coords.worldToScreen({ x, y }),
      tileCornersOnScreen: (x, y) => scene.coords.tileCornersOnScreen({ x, y }),
      terrainAt: (x, y) => scene.ctx.map.info(x, y).key,
      phaserWorldPoint: (x, y) => {
        const p = scene.cameras.main.getWorldPoint(x, y);
        return { x: p.x, y: p.y };
      },
      groundTileBounds: (x, y) => scene.mapRenderer.groundTileBounds(x, y),
      tileMetadata: (x, y) => scene.ctx.map.tileMetadata(x, y),
    };
  }

  private rectToScreen(r: { x: number; y: number; right: number; bottom: number } | null): { x: number; y: number; width: number; height: number } | null {
    if (!r) return null;
    const a = this.coords.worldToScreen({ x: r.x, y: r.y });
    const b = this.coords.worldToScreen({ x: r.right, y: r.bottom });
    return { x: a.x, y: a.y, width: b.x - a.x, height: b.y - a.y };
  }

  private teardown(): void {
    for (const u of this.unsubs) u();
    this.unsubs.length = 0;
    this.scale.off(Phaser.Scale.Events.RESIZE, this.onResize, this);
    this.inputs.destroy();
    this.unitRenderer.destroy();
    this.buildingRenderer.destroy();
    this.flashes.destroy();
    this.damageNumbers.destroy();
    this.mapRenderer.destroy();
    delete window.__AFTER_THE_FALL__;
  }
}
